import { Injectable } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpErrorResponse,
    HttpSentEvent,
    HttpHeaderResponse,
    HttpProgressEvent,
    HttpResponse,
    HttpUserEvent
  } from '@angular/common/http';

import { NgxUiLoaderService } from 'ngx-ui-loader';
import { throwError, BehaviorSubject, Observable } from 'rxjs';
import { catchError, switchMap, finalize, filter, take } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { SwalService } from 'src/app/shared/services/swal.service';
import { CommunicationService } from 'src/app/shared/services/communication.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  isRefreshingToken: boolean = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private swalService: SwalService,
    private authService: AuthService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private communicationService: CommunicationService,
  ) {}

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token }})
  }

  intercept(request: HttpRequest<any>, next: HttpHandler):
  Observable<HttpSentEvent |
  HttpHeaderResponse |
  HttpProgressEvent |
  HttpResponse<any> |
  HttpUserEvent<any>>
  {
    if (request.url.includes('login')) {
      return next.handle(request).pipe(
        catchError((err: HttpErrorResponse) => {

          if (err instanceof HttpErrorResponse) {
            this.communicationService.sendErrorOnLogin();
            this.swalService.fireErrorSwal('Não existe usuário com esses dados!');
            return throwError('Não existe usuário com esses dados!');
          }

        })
      );
    }

    // Para outras requisições, verifica se há token antes de adicionar
    const userToken = this.authService.getUserToken();
    
    if (userToken && userToken.token) {
      return next.handle(this.addToken(request, userToken.token))
        .pipe(catchError((err: HttpErrorResponse) => {

          if (err instanceof HttpErrorResponse) {
            // Se erro tem status 401 ele faz o refresh token
            if (err.status === 401) {
              this.authService.logout();
              this.swalService.fireErrorSwal('Autenticação necessária!');
              return throwError(err.error.message);
            }

            let errorMsg = '';

            if (err.status === 500) {
              errorMsg = 'Erro No Servidor!';
            } else if (err.status === 412) {
              // Tratamento específico para erro de pré-condição (usuário principal)
              if (err.error && typeof err.error === 'string') {
                errorMsg = err.error;
              } else if (err.error?.message) {
                errorMsg = err.error.message;
              } else {
                errorMsg = 'Não é possível excluir o usuário principal de uma empresa existente!';
              }
            } else {
              // Para outros erros, tenta pegar a mensagem do erro
              errorMsg = err.error?.message || err.message || 'Ocorreu um erro na operação';
            }

            if (!environment.production) {
              console.log('Erro interceptado:', err);
            }
            this.swalService.fireErrorSwal(errorMsg);
            this.ngxUiLoaderService.stopAll();

            return throwError(errorMsg);
          }
        }
      ));
    } else {
      // Se não há token, faz a requisição sem autorização
      return next.handle(request).pipe(
        catchError((err: HttpErrorResponse) => {
          if (!environment.production) {
            console.log('Erro interceptado (sem token):', err);
          }
          return throwError(err);
        })
      );
    }
  }

  handleUnauthorized(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Verifica se esse processo já está em andamento
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      this.tokenSubject.next(null);

      return this.authService.refreshToken()
        .pipe(switchMap((userToken: any) => {

          // Verifica se request retornou o token
          if (userToken !== undefined && userToken !== null) {
            localStorage.setItem('userToken', JSON.stringify(userToken));

            this.tokenSubject.next(userToken.token);

            return next.handle(this.addToken(req, userToken.token));
          }

          // Senão, faz o logout
          this.authService.logout();
          return throwError('');
        }),

        catchError(error => {
          // Caso aconteceu um erro ao fazer refresh token,
          // deslogar
          this.authService.logout();
          return throwError('');
        }),

        finalize(() => {
          this.isRefreshingToken = false;
        })
      );
    } else {

      return this.tokenSubject
        .pipe(
          filter(token => token != null)
          ,take(1)
          ,switchMap(token => {
            return next.handle(this.addToken(req, token));
          }
        )
      );
    }
  }
}
